import type { ServerSetting } from '../../../services/adminServerSettings';
import type { CapacityRefusal, UnitManagerStatus } from '../../../services/adminUnits';
import { SentenceChipStyle } from '../SettingRow';
import { formatClock, formatSpan, plural } from './unitsFormat';
import { UnitsCreationPausedKey, appliedValue } from './unitsPolicy';

/** The manager runs a round every 15 seconds; one silent for longer than this is shown as not reporting. */
export const ManagerSilentAfterMs = 2 * 60_000;

const ofImage = (imageTag: string | null) => (imageTag ? ` of image ${imageTag}` : '');

const ProminentChipStyle = { height: 32, padding: '0 14px', fontSize: 13.5 } as const;

function managerState(manager: UnitManagerStatus | null, asOf: string): { text: string; className: string } {
  if (!manager) return { text: 'Never reported', className: 'chip' };
  const silentFor = Date.parse(asOf) - Date.parse(manager.lastRoundAt);
  return silentFor <= ManagerSilentAfterMs
    ? { text: `Reporting · last round ${formatSpan(silentFor)} ago`, className: 'chip ok' }
    : { text: `Not reporting for ${formatSpan(silentFor)}`, className: 'chip warn' };
}

function Chip({ warn = false, children }: { warn?: boolean; children: string }) {
  return (
    <span className={warn ? 'chip warn' : 'chip ok'} style={SentenceChipStyle}>
      <span className="dot" style={{ flexShrink: 0 }} />
      {children}
    </span>
  );
}

const QuotaUnits: Record<string, string> = { StandardCores: 'cores', ContainerGroups: 'container groups' };

function noRoom(region: string, refusal: CapacityRefusal): string {
  const at = ` Last tried ${formatClock(refusal.at)}.`;
  const quota = refusal.quota;
  if (!quota) return `Azure refused a new group in ${region}: ${refusal.message}${at}`;
  const unit = QuotaUnits[quota.name] ?? quota.name;
  return `Quota reached in ${region}: ${quota.limit} ${unit} (${quota.usage} in use, ${quota.requested} requested).${at}`;
}

function creationHolds(manager: UnitManagerStatus | null, pausedByAdmin: boolean, asOf: string): string[] {
  const holds: string[] = [];
  if (pausedByAdmin) holds.push('Unit creation is paused by the admin.');
  if (manager?.backOffUntil && Date.parse(manager.backOffUntil) > Date.parse(asOf)) {
    const failures = plural(manager.failedStartsInARow, 'failed start', 'failed starts');
    holds.push(
      `Unit creation is paused until ${formatClock(manager.backOffUntil)} after ${failures} in a row` +
        `${ofImage(manager.failedStartsImageTag)}.`
    );
  }
  if (manager?.capacityRefusal) holds.push(noRoom(manager.region, manager.capacityRefusal));
  return holds;
}

interface CreationStateProps {
  manager: UnitManagerStatus | null;
  settings: ServerSetting[];
  asOf: string;
}

function CreationState({ manager, settings, asOf }: CreationStateProps) {
  const pausedByAdmin = appliedValue(settings, UnitsCreationPausedKey) === 'true';
  const holds = creationHolds(manager, pausedByAdmin, asOf);
  if (holds.length > 0) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {holds.map((hold) => (
          <Chip key={hold} warn>{hold}</Chip>
        ))}
      </div>
    );
  }
  return (
    <span className="muted">
      {manager && manager.failedStartsInARow > 0
        ? `${plural(manager.failedStartsInARow, 'failed start', 'failed starts')} in a row${ofImage(manager.failedStartsImageTag)}.`
        : 'Unit creation is not paused.'}
    </span>
  );
}

interface UnitManagerCardProps {
  /** Null while the manager never wrote its status. */
  manager: UnitManagerStatus | null;
  /** When the server read the status; the silence is measured from it. */
  asOf: string;
  /** Null until loaded; the admin pause comes from them. */
  settings: ServerSetting[] | null;
}

export default function UnitManagerCard({ manager, asOf, settings }: UnitManagerCardProps) {
  const state = managerState(manager, asOf);
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span className={state.className} style={ProminentChipStyle} role="status" aria-label="Unit manager state">
          <span className="dot" />
          {state.text}
        </span>
        {manager && (
          <span className="muted">
            Host <span className="mono">{manager.managerHost}</span> · region <span className="mono">{manager.region}</span>
          </span>
        )}
      </div>
      {settings && <CreationState manager={manager} settings={settings} asOf={asOf} />}
    </div>
  );
}
